/**
 * AIBrainClient - Client-side AI behavior system for offline mode
 *
 * This is a port of the server-side AIBrain system, adapted for client-side
 * use in offline/practice mode. Implements a state machine with personality-based
 * decision making, predictive hunting, smart evasion, and collision avoidance.
 *
 * Uses the 4-tier personality system: Collector, Hunter, Apex, Legendary.
 */

import {
  AIState,
  AIPersonality,
  AIConfig,
  DEFAULT_AI_CONFIG,
  PowerupType,
} from '@wooorms/shared';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Minimal worm interface for AI calculations
 */
export interface ClientAIWormData {
  id: string;
  index: number;
  isAI: boolean;
  alive: boolean;
  score: number;
  angle: number;
  boosting: boolean;
  headX: number;
  headY: number;
  radius: number;
  segments: Array<{ x: number; y: number; radius: number }>;
  aiState: AIState;
  aiThinkTimer: number;
  personality: AIPersonality;
}

/**
 * Food data for AI targeting
 */
export interface ClientAIFoodData {
  id: string;
  x: number;
  y: number;
  value: number;
  radius: number;
  fromDeath?: boolean;
}

/**
 * Powerup data for AI targeting
 */
export interface ClientAIPowerUpData {
  id: string;
  x: number;
  y: number;
  radius: number;
  type: string;
}

/**
 * Game state data needed by AI
 */
export interface ClientAIGameState {
  worms: ClientAIWormData[];
  playerWorm: ClientAIWormData | null;
  foods: ClientAIFoodData[];
  powerUps: ClientAIPowerUpData[];
  worldRadius: number;
}

/**
 * AI decision output
 */
export interface AIDecision {
  targetAngle: number;
  sprinting: boolean;
  crawling?: boolean; // LEVIATHAN can crawl for sharper turns
  newState: AIState;
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Calculate distance between two points
 */
function dist(x1: number, y1: number, x2: number, y2: number): number {
  return Math.hypot(x2 - x1, y2 - y1);
}

/**
 * Calculate distance from world center
 */
function distFromCenter(x: number, y: number): number {
  return Math.hypot(x, y);
}

/**
 * Normalize angle to -PI to PI range
 */
function normalizeAngle(a: number): number {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}

/**
 * Random value in range
 */
function randomInRange(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}

/**
 * Check if a point is too close to any worm segment (collision danger)
 */
function isPointNearBody(
  x: number,
  y: number,
  worms: ClientAIWormData[],
  myId: string,
  dangerRadius: number
): { collision: boolean; nearestAngle: number | null; nearestDist: number } {
  let nearestDist = Infinity;
  let nearestAngle: number | null = null;

  for (const worm of worms) {
    if (!worm.alive) continue;

    // Skip own head but check own body (avoid self-collision)
    const startIndex = worm.id === myId ? 5 : 0;

    for (let i = startIndex; i < worm.segments.length; i++) {
      const seg = worm.segments[i];
      if (!seg) continue;

      const d = dist(x, y, seg.x, seg.y);
      const threshold = dangerRadius + seg.radius;

      if (d < threshold && d < nearestDist) {
        nearestDist = d;
        nearestAngle = Math.atan2(y - seg.y, x - seg.x);
      }
    }
  }

  return {
    collision: nearestDist < dangerRadius * 1.5,
    nearestAngle,
    nearestDist,
  };
}

/**
 * Scan ahead on a path to check for collisions
 */
function scanPath(
  startX: number,
  startY: number,
  angle: number,
  distance: number,
  worms: ClientAIWormData[],
  myId: string,
  dangerRadius: number
): boolean {
  const steps = 5;
  const stepSize = distance / steps;

  for (let i = 1; i <= steps; i++) {
    const checkX = startX + Math.cos(angle) * stepSize * i;
    const checkY = startY + Math.sin(angle) * stepSize * i;

    const result = isPointNearBody(checkX, checkY, worms, myId, dangerRadius);
    if (result.collision) {
      return false; // Path is blocked
    }
  }

  return true; // Path is clear
}

/**
 * Find the safest direction to move
 */
function findSafeDirection(
  head: { x: number; y: number },
  currentAngle: number,
  worms: ClientAIWormData[],
  myId: string,
  dangerRadius: number,
  worldRadius: number
): number {
  // Check multiple directions and find the best one
  const directions = [
    0, // Current direction
    Math.PI / 6, // 30 degrees right
    -Math.PI / 6, // 30 degrees left
    Math.PI / 4, // 45 degrees right
    -Math.PI / 4, // 45 degrees left
    Math.PI / 3, // 60 degrees right
    -Math.PI / 3, // 60 degrees left
    Math.PI / 2, // 90 degrees right
    -Math.PI / 2, // 90 degrees left
  ];

  let bestDirection = currentAngle;
  let bestScore = -Infinity;

  for (const offset of directions) {
    const testAngle = normalizeAngle(currentAngle + offset);
    const testDist = 150; // Scan distance

    // Check if this direction is safe
    const pathClear = scanPath(head.x, head.y, testAngle, testDist, worms, myId, dangerRadius);
    if (!pathClear) continue;

    // Calculate score based on:
    // 1. Prefer directions close to original
    // 2. Avoid edges
    const testX = head.x + Math.cos(testAngle) * testDist;
    const testY = head.y + Math.sin(testAngle) * testDist;
    const edgeDist = worldRadius - distFromCenter(testX, testY);

    const angleScore = 1 - Math.abs(offset) / Math.PI;
    const edgeScore = Math.min(1, edgeDist / 300);
    const score = angleScore * 0.6 + edgeScore * 0.4;

    if (score > bestScore) {
      bestScore = score;
      bestDirection = testAngle;
    }
  }

  return bestDirection;
}

/**
 * Calculate food density around a point
 */
function getFoodDensity(
  x: number,
  y: number,
  foods: ClientAIFoodData[],
  radius: number
): number {
  let density = 0;
  for (const food of foods) {
    const d = dist(x, y, food.x, food.y);
    if (d < radius) {
      density += food.value * (1 - d / radius);
    }
  }
  return density;
}

// =============================================================================
// AI BRAIN CLASS
// =============================================================================

/**
 * AIBrainClient - Client-side AI decision-making system for offline mode
 * All methods are static for functional-style usage
 */
export class AIBrainClient {
  /**
   * Main think method - called each tick for AI worms
   *
   * @param worm - The AI worm to make decisions for
   * @param gameState - Current game state
   * @param config - AI configuration (optional)
   * @returns AI decision with target angle and sprint state
   */
  static think(
    worm: ClientAIWormData,
    gameState: ClientAIGameState,
    config: AIConfig = DEFAULT_AI_CONFIG
  ): AIDecision {
    // Get head position
    const head = { x: worm.headX, y: worm.headY };

    // Build combined worms list for collision/threat detection
    const allWorms = this._buildAllWormsArray(worm, gameState);

    // Get personality from worm
    const personality: AIPersonality = worm.personality;

    // Calculate edge distance
    const edgeDist = distFromCenter(head.x, head.y);
    const edgeDanger = edgeDist > gameState.worldRadius - config.edgeDangerDistance;

    // Detect threats (bigger worms facing us)
    const threat = this._detectThreat(worm, allWorms, config);

    // Determine new state based on priorities
    let newState: AIState = worm.aiState;

    if (edgeDanger) {
      newState = AIState.FLEE_EDGE;
    } else if (threat && personality.riskTolerance < 0.4) {
      newState = AIState.EVADE;
    } else {
      // Check for nearby powerups - priority based on personality
      const nearbyPowerUp = this._findNearbyPowerUp(head, gameState.powerUps, config, personality);
      if (nearbyPowerUp) {
        const powerUpDist = dist(head.x, head.y, nearbyPowerUp.x, nearbyPowerUp.y);
        const powerUpPriority = this._getPowerupPriorityForPersonality(nearbyPowerUp.type, personality);

        // Decision to pursue powerup based on:
        // - Distance (always go if very close)
        // - Powerup priority for this personality
        // - Personality's food weight (collectors more likely to go for powerups)
        const baseChance = 0.5 + (personality.foodWeight * 0.3); // 0.5 to 0.8 base chance
        const priorityBonus = (powerUpPriority - 1.0) * 0.15; // Up to 0.375 extra for high priority powerups
        const pursueChance = Math.min(0.95, baseChance + priorityBonus);

        // Very close powerups are always pursued
        // Otherwise, chance is based on personality and powerup type
        if (powerUpDist < 150 || Math.random() < pursueChance) {
          newState = AIState.COLLECT_POWERUP;
        }
      }

      if (newState !== AIState.COLLECT_POWERUP) {
        // Decide between hunting players and hunting food based on personality
        const shouldHuntPlayer = this._shouldHuntPlayer(
          worm,
          allWorms,
          personality,
          config
        );
        newState = shouldHuntPlayer ? AIState.HUNT_PLAYER : AIState.HUNT_FOOD;
      }
    }

    // Execute state behavior
    const decision = this._executeState(worm, newState, gameState, allWorms, personality, config, threat);

    // COLLISION AVOIDANCE - Final safety check
    // Scan ahead to see if we're about to hit something
    const dangerRadius = worm.radius * 2;
    const scanDist = 100;
    const pathClear = scanPath(head.x, head.y, decision.targetAngle, scanDist, allWorms, worm.id, dangerRadius);

    if (!pathClear && newState !== AIState.FLEE_EDGE) {
      // Path is blocked - find a safe direction
      const safeAngle = findSafeDirection(
        head,
        decision.targetAngle,
        allWorms,
        worm.id,
        dangerRadius,
        gameState.worldRadius
      );

      return {
        targetAngle: safeAngle,
        sprinting: false, // Don't sprint when avoiding collision
        newState,
      };
    }

    return {
      ...decision,
      newState,
    };
  }

  /**
   * Build combined worms array from AI worms and player worm
   */
  private static _buildAllWormsArray(
    _currentWorm: ClientAIWormData,
    gameState: ClientAIGameState
  ): ClientAIWormData[] {
    const allWorms: ClientAIWormData[] = [...gameState.worms];
    if (gameState.playerWorm && gameState.playerWorm.alive) {
      allWorms.push(gameState.playerWorm);
    }
    return allWorms;
  }

  /**
   * Detect if there's a threat nearby
   */
  private static _detectThreat(
    worm: ClientAIWormData,
    allWorms: ClientAIWormData[],
    config: AIConfig
  ): ClientAIWormData | null {
    const head = { x: worm.headX, y: worm.headY };

    for (const other of allWorms) {
      if (!other.alive || other.id === worm.id) continue;

      const otherHead = { x: other.headX, y: other.headY };
      const d = dist(head.x, head.y, otherHead.x, otherHead.y);

      // Check if other worm is close and bigger (only fear worms 20%+ larger)
      if (d < config.threatDetectionRadius && other.score > worm.score * 1.2) {
        // Check if they're facing us (angle to us is within ~70 degrees of their facing)
        const angleToUs = Math.atan2(head.y - otherHead.y, head.x - otherHead.x);
        if (Math.abs(normalizeAngle(other.angle - angleToUs)) < Math.PI / 2.5) {
          return other;
        }
      }
    }

    return null;
  }

  /**
   * Get powerup priority score based on AI personality
   * Different personalities value different powerups
   */
  private static _getPowerupPriorityForPersonality(
    powerupType: string,
    personality: AIPersonality
  ): number {
    // Base priority - all powerups are somewhat desirable
    let priority = 1.0;

    // Personality-based scoring
    const personalityType = personality.type;

    switch (powerupType) {
      case PowerupType.SPEED:
        // Aggressive hunters love speed for chasing prey
        if (personalityType === 'Apex' || personalityType === 'Legendary') {
          priority = 2.5; // Very high priority
        } else if (personalityType === 'Hunter') {
          priority = 2.0; // High priority
        } else {
          priority = 1.5; // Collectors prefer safety, but speed helps escape
        }
        break;

      case PowerupType.AGILITY:
        // Good for all playstyles - helps dodge and maneuver
        if (personalityType === 'Apex' || personalityType === 'Legendary') {
          priority = 2.0; // Helps cut off prey
        } else if (personalityType === 'Collector') {
          priority = 2.2; // Collectors love evasion ability
        } else {
          priority = 1.8;
        }
        break;

      case PowerupType.MAGNET:
        // Collectors love magnets for efficient food gathering
        if (personalityType === 'Collector') {
          priority = 2.8; // Highest priority for collectors
        } else if (personalityType === 'Hunter') {
          priority = 1.8; // Still useful for growing
        } else {
          priority = 1.2; // Apex/Legendary prefer hunting over gathering
        }
        break;

      case PowerupType.MULTIPLIER_2X:
        // Multipliers help everyone grow faster
        if (personalityType === 'Collector') {
          priority = 2.5;
        } else {
          priority = 1.8;
        }
        break;

      case PowerupType.MULTIPLIER_4X:
        // Higher multipliers are more valuable
        if (personalityType === 'Collector') {
          priority = 3.0;
        } else {
          priority = 2.2;
        }
        break;

      case PowerupType.MULTIPLIER_6X:
        // 6x is always high priority for everyone
        priority = 3.5;
        break;

      default:
        priority = 1.0;
    }

    return priority;
  }

  /**
   * Find a nearby powerup worth pursuing based on personality
   * Returns the best powerup considering distance and personality-based priority
   */
  private static _findNearbyPowerUp(
    head: { x: number; y: number },
    powerUps: ClientAIPowerUpData[],
    config: AIConfig,
    personality?: AIPersonality
  ): ClientAIPowerUpData | null {
    let bestPowerUp: ClientAIPowerUpData | null = null;
    let bestScore = 0;

    for (const pu of powerUps) {
      const d = dist(head.x, head.y, pu.x, pu.y);
      if (d > config.foodSearchRadius) continue;

      // Calculate priority based on personality
      const typePriority = personality
        ? this._getPowerupPriorityForPersonality(pu.type, personality)
        : 1.0;

      // Score = priority / (distance + small offset to prevent division issues)
      // Closer powerups with higher priority score better
      const score = typePriority * 100 / (d + 50);

      if (score > bestScore) {
        bestScore = score;
        bestPowerUp = pu;
      }
    }

    return bestPowerUp;
  }

  /**
   * Decide if AI should hunt a player or another worm
   */
  private static _shouldHuntPlayer(
    worm: ClientAIWormData,
    allWorms: ClientAIWormData[],
    personality: AIPersonality,
    config: AIConfig
  ): boolean {
    const head = { x: worm.headX, y: worm.headY };

    // Find potential prey (smaller worms nearby - including other AI worms)
    for (const other of allWorms) {
      if (!other.alive || other.id === worm.id) continue;

      const otherHead = { x: other.headX, y: other.headY };
      const d = dist(head.x, head.y, otherHead.x, otherHead.y);

      // Check if we're bigger enough and close enough
      if (
        d < config.huntRadius &&
        worm.score > other.score * config.huntScoreRatio
      ) {
        // Personality-based decision - AI is MORE motivated to hunt players (1.5x), less to hunt other AI (0.5x)
        const huntChance = other.isAI ? personality.huntWeight * 0.5 : personality.huntWeight * 1.5;
        return Math.random() < huntChance;
      }
    }

    return false;
  }

  /**
   * Execute the current state and return decision
   */
  private static _executeState(
    worm: ClientAIWormData,
    state: AIState,
    gameState: ClientAIGameState,
    allWorms: ClientAIWormData[],
    _personality: AIPersonality,
    config: AIConfig,
    threat: ClientAIWormData | null
  ): { targetAngle: number; sprinting: boolean } {
    const head = { x: worm.headX, y: worm.headY };

    switch (state) {
      case AIState.FLEE_EDGE:
        return this._fleeEdge(head, worm.score, config);

      case AIState.EVADE:
        return this._evade(head, worm.score, threat, config, allWorms, worm.id, gameState.worldRadius);

      case AIState.HUNT_PLAYER:
        return this._huntPlayer(worm, allWorms, config);

      case AIState.COLLECT_POWERUP:
        return this._collectPowerUp(worm, gameState, config);

      case AIState.HUNT_FOOD:
      default:
        return this._huntFood(worm, gameState, config);
    }
  }

  /**
   * FLEE_EDGE state - return to center when near boundary
   */
  private static _fleeEdge(
    head: { x: number; y: number },
    score: number,
    config: AIConfig
  ): { targetAngle: number; sprinting: boolean } {
    // Point toward center (0, 0)
    const targetAngle = Math.atan2(-head.y, -head.x);
    const sprinting = score > config.minSprintScore + 15;

    return { targetAngle, sprinting };
  }

  /**
   * EVADE state - run away from threats with smart evasion patterns
   */
  private static _evade(
    head: { x: number; y: number },
    score: number,
    threat: ClientAIWormData | null,
    config: AIConfig,
    worms: ClientAIWormData[] = [],
    myId: string = '',
    worldRadius: number = 4000
  ): { targetAngle: number; sprinting: boolean } {
    let targetAngle = Math.random() * Math.PI * 2;

    if (threat) {
      const threatHead = { x: threat.headX, y: threat.headY };

      // Base escape direction - away from threat
      const baseEscapeAngle = Math.atan2(head.y - threatHead.y, head.x - threatHead.x);
      const threatDist = dist(head.x, head.y, threatHead.x, threatHead.y);

      // Calculate if threat is chasing directly or from an angle
      const angleToUs = Math.atan2(head.y - threatHead.y, head.x - threatHead.x);
      const threatChaseAngle = Math.abs(normalizeAngle(threat.angle - angleToUs));

      if (threatDist < 150) {
        // VERY CLOSE - use sharp turn evasion
        // Turn perpendicular to escape direction to throw off pursuer
        const perpOffset = Math.random() > 0.5 ? Math.PI / 2 : -Math.PI / 2;
        targetAngle = baseEscapeAngle + perpOffset;
      } else if (threatDist < 300 && threatChaseAngle < Math.PI / 4) {
        // Close and being directly chased - use zigzag pattern
        // Alternate between left and right escape angles
        const zigzagOffset = (Math.sin(Date.now() * 0.008) > 0 ? 1 : -1) * Math.PI / 3;
        targetAngle = baseEscapeAngle + zigzagOffset;
      } else if (threatDist < 400) {
        // Medium range - calculate escape route that avoids edge
        const escapeX = head.x + Math.cos(baseEscapeAngle) * 200;
        const escapeY = head.y + Math.sin(baseEscapeAngle) * 200;
        const escapeEdgeDist = worldRadius - distFromCenter(escapeX, escapeY);

        if (escapeEdgeDist < 200) {
          // Would escape toward edge - turn toward center instead
          const toCenterAngle = Math.atan2(-head.y, -head.x);
          // Blend escape direction with toward-center direction
          targetAngle = normalizeAngle((baseEscapeAngle + toCenterAngle) / 2);
        } else {
          targetAngle = baseEscapeAngle + randomInRange(-0.3, 0.3);
        }
      } else {
        // Far enough - can try a sneaky double-back maneuver
        // Sometimes go perpendicular to throw off predators
        if (Math.random() < 0.3) {
          targetAngle = baseEscapeAngle + (Math.random() > 0.5 ? Math.PI / 2 : -Math.PI / 2);
        } else {
          targetAngle = baseEscapeAngle + randomInRange(-0.5, 0.5);
        }
      }

      // Ensure escape path doesn't hit other worms (if worms data available)
      if (worms.length > 0 && myId) {
        const pathClear = scanPath(head.x, head.y, targetAngle, 100, worms, myId, 15);
        if (!pathClear) {
          // Try alternate directions
          for (const offset of [Math.PI / 4, -Math.PI / 4, Math.PI / 2, -Math.PI / 2]) {
            const altAngle = normalizeAngle(targetAngle + offset);
            if (scanPath(head.x, head.y, altAngle, 100, worms, myId, 15)) {
              targetAngle = altAngle;
              break;
            }
          }
        }
      }
    }

    // Always sprint when evading if we can afford it
    const sprinting = score > config.minSprintScore;

    return { targetAngle, sprinting };
  }

  /**
   * HUNT_PLAYER state - chase other worms with smart interception
   */
  private static _huntPlayer(
    worm: ClientAIWormData,
    allWorms: ClientAIWormData[],
    config: AIConfig
  ): { targetAngle: number; sprinting: boolean } {
    const head = { x: worm.headX, y: worm.headY };

    // Find best target (closest worm that we're bigger than - including AI and player)
    let bestTarget: ClientAIWormData | null = null;
    let bestScore = -Infinity;

    for (const other of allWorms) {
      if (!other.alive || other.id === worm.id) continue;

      const otherHead = { x: other.headX, y: other.headY };

      // Must be bigger enough to hunt
      if (worm.score <= other.score * config.huntScoreRatio) continue;

      const d = dist(head.x, head.y, otherHead.x, otherHead.y);
      if (d > config.huntRadius) continue;

      // Score based on: distance (closer = better), size difference (smaller = better target)
      // Players are slightly more attractive targets than AI
      const distScore = 1 - d / config.huntRadius;
      const sizeScore = 1 - other.score / worm.score;
      const targetBonus = other.isAI ? 0 : 0.15; // Prefer hunting players slightly
      const score = distScore * 0.6 + sizeScore * 0.4 + targetBonus;

      if (score > bestScore) {
        bestScore = score;
        bestTarget = other;
      }
    }

    if (!bestTarget) {
      // Fall back to wandering
      return {
        targetAngle: worm.angle + randomInRange(-0.4, 0.4),
        sprinting: false,
      };
    }

    const targetHead = { x: bestTarget.headX, y: bestTarget.headY };
    const targetDist = dist(head.x, head.y, targetHead.x, targetHead.y);

    // SMART INTERCEPTION STRATEGY
    // Calculate where the target is heading and try to cut them off
    const targetSpeed = bestTarget.boosting ? 8 : 4;
    const predictionTime = Math.min(targetDist / 6, 30); // How many frames to predict

    // Predict target position
    const predictedX = targetHead.x + Math.cos(bestTarget.angle) * targetSpeed * predictionTime;
    const predictedY = targetHead.y + Math.sin(bestTarget.angle) * targetSpeed * predictionTime;

    // Check if we can intercept by going slightly ahead of them
    const interceptX = predictedX + Math.cos(bestTarget.angle) * 50;
    const interceptY = predictedY + Math.sin(bestTarget.angle) * 50;

    // Calculate angle to intercept point (cut them off)
    let targetAngle: number;

    if (targetDist < 200) {
      // Very close - try to get in front of them
      const angleToTarget = Math.atan2(targetHead.y - head.y, targetHead.x - head.x);
      const angleDiff = normalizeAngle(bestTarget.angle - angleToTarget);

      // If we're behind them, try to swing around to cut off
      if (Math.abs(angleDiff) < Math.PI / 2) {
        // We're roughly behind - try to get to the side
        const sideOffset = angleDiff > 0 ? Math.PI / 3 : -Math.PI / 3;
        targetAngle = Math.atan2(
          interceptY - head.y + Math.sin(bestTarget.angle + sideOffset) * 80,
          interceptX - head.x + Math.cos(bestTarget.angle + sideOffset) * 80
        );
      } else {
        // We're in front or to the side - aim at their predicted position
        targetAngle = Math.atan2(predictedY - head.y, predictedX - head.x);
      }
    } else if (targetDist < 400) {
      // Medium range - intercept their path
      targetAngle = Math.atan2(interceptY - head.y, interceptX - head.x);
    } else {
      // Far away - just head toward their predicted position
      targetAngle = Math.atan2(predictedY - head.y, predictedX - head.x);
    }

    // Sprint if close enough and we have enough score
    const sprinting = targetDist < 300 && worm.score > config.minSprintScore + 10;

    return { targetAngle, sprinting };
  }

  /**
   * COLLECT_POWERUP state - go for nearby powerups with personality-based behavior
   */
  private static _collectPowerUp(
    worm: ClientAIWormData,
    gameState: ClientAIGameState,
    config: AIConfig
  ): { targetAngle: number; sprinting: boolean } {
    const head = { x: worm.headX, y: worm.headY };
    const personality = worm.personality;

    // Find best powerup using personality-weighted scoring
    const bestPowerUp = this._findNearbyPowerUp(head, gameState.powerUps, config, personality);

    if (!bestPowerUp) {
      // Fall back to hunting food
      return this._huntFood(worm, gameState, config);
    }

    const powerUpDist = dist(head.x, head.y, bestPowerUp.x, bestPowerUp.y);
    const targetAngle = Math.atan2(bestPowerUp.y - head.y, bestPowerUp.x - head.x);

    // Determine if we should sprint for this powerup
    // More aggressive personalities sprint more readily
    // High-priority powerups warrant more aggressive pursuit
    const powerUpPriority = this._getPowerupPriorityForPersonality(bestPowerUp.type, personality);
    const canSprint = worm.score > config.minSprintScore;

    let shouldSprint = false;
    if (canSprint) {
      // Sprint conditions:
      // 1. High priority powerup (2.5+) and within moderate range
      // 2. Any powerup very close (grab it fast!)
      // 3. Aggressive personality (high risk tolerance) for medium-priority powerups
      if (powerUpDist < 100) {
        // Very close - sprint to secure it
        shouldSprint = true;
      } else if (powerUpDist < 200 && powerUpPriority >= 2.5) {
        // High priority powerup in medium range - sprint!
        shouldSprint = true;
      } else if (powerUpDist < 300 && powerUpPriority >= 2.0 && personality.riskTolerance > 0.6) {
        // Aggressive AI sprints for good powerups
        shouldSprint = true;
      }
    }

    return { targetAngle, sprinting: shouldSprint };
  }

  /**
   * HUNT_FOOD state - seek best nearby food with density awareness
   */
  private static _huntFood(
    worm: ClientAIWormData,
    gameState: ClientAIGameState,
    config: AIConfig
  ): { targetAngle: number; sprinting: boolean } {
    const head = { x: worm.headX, y: worm.headY };

    // First, check for death food (high priority - it's very valuable)
    let deathFoodTarget: ClientAIFoodData | null = null;
    let deathFoodDist = Infinity;

    for (const food of gameState.foods) {
      if (!food.fromDeath) continue;
      const d = dist(head.x, head.y, food.x, food.y);
      if (d < deathFoodDist && d < config.foodSearchRadius * 1.2) {
        deathFoodDist = d;
        deathFoodTarget = food;
      }
    }

    // If there's death food nearby, go for it with urgency
    if (deathFoodTarget && deathFoodDist < 400) {
      const targetAngle = Math.atan2(deathFoodTarget.y - head.y, deathFoodTarget.x - head.x);
      const sprinting = deathFoodDist < 200 && worm.score > config.minSprintScore;
      return { targetAngle, sprinting };
    }

    // Find best food considering both individual value and nearby food density
    let bestFood: ClientAIFoodData | null = null;
    let bestScore = 0;

    for (const food of gameState.foods) {
      const d = dist(head.x, head.y, food.x, food.y);
      if (d > config.foodSearchRadius) continue;

      // Calculate base value/distance score
      const valueMultiplier = food.fromDeath ? 3.0 : 1;
      const baseValue = (food.value * valueMultiplier) / (d + 20);

      // Add bonus for food clusters (more food nearby = better destination)
      const densityBonus = getFoodDensity(food.x, food.y, gameState.foods, 150) * 0.01;

      const score = baseValue + densityBonus;

      if (score > bestScore) {
        bestScore = score;
        bestFood = food;
      }
    }

    if (!bestFood) {
      // Smart wandering - head toward areas with food density
      // Sample a few random directions and pick the one with most food
      let bestWanderAngle = worm.angle;
      let bestWanderDensity = 0;

      for (let i = 0; i < 6; i++) {
        const testAngle = worm.angle + randomInRange(-Math.PI, Math.PI);
        const testX = head.x + Math.cos(testAngle) * 300;
        const testY = head.y + Math.sin(testAngle) * 300;

        // Avoid edge in wandering
        const edgeDist = gameState.worldRadius - distFromCenter(testX, testY);
        if (edgeDist < 200) continue;

        const density = getFoodDensity(testX, testY, gameState.foods, 300);
        if (density > bestWanderDensity) {
          bestWanderDensity = density;
          bestWanderAngle = testAngle;
        }
      }

      // If no good direction found, just wander with small adjustments
      if (bestWanderDensity === 0) {
        bestWanderAngle = worm.angle + randomInRange(-0.4, 0.4);
      }

      return {
        targetAngle: bestWanderAngle,
        sprinting: false,
      };
    }

    const targetAngle = Math.atan2(bestFood.y - head.y, bestFood.x - head.x);
    const foodDist = dist(head.x, head.y, bestFood.x, bestFood.y);

    // Sprint toward death food if it's moderate distance away
    const sprinting = Boolean(bestFood.fromDeath) && foodDist > 150 && foodDist < 350 && worm.score > config.minSprintScore;

    return { targetAngle, sprinting };
  }
}
